import type { AtlasLocale } from "./locale";

export type ReaderCrystalReference = {
  kind: "crystal";
  chapterSlug: string;
  crystalId: string;
};

export type ReaderEssayReference = {
  kind: "essay";
  slug: string;
};

export type ReaderMaterialReference =
  | ReaderCrystalReference
  | ReaderEssayReference;

export type ReaderRouteId = "seeing" | "decision" | "limits";

export type ReaderRouteSpec = {
  id: ReaderRouteId;
  materials: readonly ReaderMaterialReference[];
  exercise: ReaderCrystalReference;
};

export const READER_ROUTE_STEP_COUNT = 5;

export const readerRouteSpecs = [
  {
    id: "seeing",
    materials: [
      { kind: "crystal", chapterSlug: "reality", crystalId: "reality-trace-before-story" },
      { kind: "crystal", chapterSlug: "systems", crystalId: "systems-pattern-reveals-structure" },
      { kind: "crystal", chapterSlug: "visibility", crystalId: "power-legibility-translates-reality" },
      { kind: "essay", slug: "map-knows-it-is-map" },
    ],
    exercise: {
      kind: "crystal",
      chapterSlug: "narrative-status",
      crystalId: "narrative-selects-cause-and-action",
    },
  },
  {
    id: "decision",
    materials: [
      { kind: "crystal", chapterSlug: "uncertainty", crystalId: "decision-process-and-outcome" },
      { kind: "crystal", chapterSlug: "plural-values", crystalId: "values-three-questions" },
      { kind: "crystal", chapterSlug: "risk", crystalId: "risk-protect-future-agency" },
      { kind: "essay", slug: "strategy-as-refusal" },
    ],
    exercise: {
      kind: "crystal",
      chapterSlug: "uncertainty",
      crystalId: "decision-preserve-future-agency",
    },
  },
  {
    id: "limits",
    materials: [
      { kind: "crystal", chapterSlug: "limits", crystalId: "limit-capacity-changes" },
      { kind: "crystal", chapterSlug: "freedom", crystalId: "freedom-dependence-can-enable-agency" },
      { kind: "crystal", chapterSlug: "finitude", crystalId: "finitude-care-changes-reality" },
      { kind: "essay", slug: "there-is-no-last-arena" },
    ],
    exercise: {
      kind: "crystal",
      chapterSlug: "limits",
      crystalId: "limit-help-as-agency",
    },
  },
] as const satisfies readonly ReaderRouteSpec[];

export function readerMaterialKey(reference: ReaderMaterialReference) {
  return reference.kind === "crystal"
    ? `crystal:${reference.chapterSlug}:${reference.crystalId}`
    : `essay:${reference.slug}`;
}

export function readerMaterialHref(
  reference: ReaderMaterialReference,
  options: { resume?: boolean } = {},
) {
  if (reference.kind === "essay") return `/essay/${reference.slug}`;
  const chapterHref = `/chapter/${reference.chapterSlug}`;
  return options.resume
    ? chapterHref
    : `${chapterHref}#crystal-${reference.crystalId}`;
}

export function readerRouteStep(routeId: ReaderRouteId, stepIndex: number) {
  const route = readerRouteSpecs.find((candidate) => candidate.id === routeId);
  if (!route) return null;
  const steps: readonly ReaderMaterialReference[] = [...route.materials, route.exercise];
  return steps[stepIndex - 1] ?? null;
}

export function findReaderRouteStep(reference: ReaderMaterialReference) {
  const materialKey = readerMaterialKey(reference);
  for (const route of readerRouteSpecs) {
    const steps: readonly ReaderMaterialReference[] = [...route.materials, route.exercise];
    const stepIndex = steps.findIndex((step) => readerMaterialKey(step) === materialKey);
    if (stepIndex >= 0) return { routeId: route.id, stepIndex: stepIndex + 1 };
  }
  return null;
}

export function resolveReaderCrystal(
  reference: ReaderCrystalReference,
  chapterMap: AtlasLocale["chapterMap"],
) {
  const chapter = chapterMap.get(reference.chapterSlug);
  const crystal = chapter?.crystals.find((item) => item.id === reference.crystalId);
  if (!chapter || !crystal) return null;
  return {
    kind: "crystal" as const,
    id: crystal.id,
    reference,
    href: readerMaterialHref(reference),
    number: chapter.number,
    status: crystal.status,
    title: crystal.title,
    question: crystal.question,
  };
}

export function resolveReaderMaterial(
  material: ReaderMaterialReference,
  chapterMap: AtlasLocale["chapterMap"],
  essayMap: AtlasLocale["essayMap"],
) {
  if (material.kind === "crystal") return resolveReaderCrystal(material, chapterMap);
  const essay = essayMap.get(material.slug);
  if (!essay) return null;
  return {
    kind: "essay" as const,
    id: essay.slug,
    reference: material,
    href: readerMaterialHref(material),
    number: essay.number,
    readingTime: essay.readingTime,
    title: essay.title,
  };
}

